import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { forkJoin } from 'rxjs';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Download, LucideAngularModule, RotateCcw, Upload } from 'lucide-angular';
import { DEFAULT_PAGE_SIZE_OPTIONS } from '../../../core/constants/pagination.constants';
import { ApprovalFile, ApprovalFileListResponse, Contract } from '../../../core/models/domain.models';
import { ApiService } from '../../../core/services/api.service';
import { ToastService } from '../../../shared/ui/toast.service';
import { displayApplicationType } from '../application-type.util';

interface ApplicationApi {
  id: string;
  application_number: string;
  contract_id: string;
  application_type: string;
  application_date: string;
  status: string;
  total_quantum?: number | string | null;
  approved_quantum?: number | string | null;
  approval_status?: string | null;
  approval_available?: boolean;
  approval_date?: string | null;
  approval_charges?: ApplicationChargeApi[];
  seller_vendor_id?: string | null;
  buyer_vendor_id?: string | null;
  schedules?: ApplicationScheduleApi[];
}

interface ApplicationChargeApi {
  group_name: string;
  section_heading?: string | null;
  sub_group_name: string;
  rate_mwh?: number | null;
  quantum_mwh?: number | null;
  amount?: number | null;
}

interface ApplicationScheduleApi {
  from_date: string;
  to_date: string;
  from_time: string;
  to_time: string;
  quantum?: number | string | null;
}

type SearchMode = 'application' | 'contract';
type DerivedApprovalState = 'approved' | 'partial-approved' | 'pending';
type ApprovalStatusFilter = 'all' | DerivedApprovalState;
type ApprovalFilters = {
  searchBy: SearchMode;
  searchText: string;
  status: ApprovalStatusFilter;
  fromDate: string;
  toDate: string;
};

@Component({
  standalone: true,
  selector: 'app-application-approval-page',
  imports: [CommonModule, FormsModule, RouterLink, LucideAngularModule],
  templateUrl: './application-approval.page.html',
  styleUrl: './application-approval.page.scss',
})
export class ApplicationApprovalPage implements OnInit {
  private readonly api = inject(ApiService);
  private readonly toast = inject(ToastService);

  protected readonly UploadIcon = Upload;
  protected readonly DownloadIcon = Download;
  protected readonly RotateCcwIcon = RotateCcw;
  protected readonly ChevronsLeftIcon = ChevronsLeft;
  protected readonly ChevronLeftIcon = ChevronLeft;
  protected readonly ChevronRightIcon = ChevronRight;
  protected readonly ChevronsRightIcon = ChevronsRight;

  protected readonly loading = signal(false);
  protected readonly uploadingByApp = signal<Record<string, boolean>>({});
  protected readonly applications = signal<ApplicationApi[]>([]);
  protected readonly contracts = signal<Contract[]>([]);
  protected readonly approvalFilesByApp = signal<Record<string, ApprovalFile[]>>({});
  protected readonly selectedApprovalDetailId = signal<string | null>(null);
  protected readonly currentPage = signal(1);
  protected readonly pageSize = signal(10);
  protected readonly pageSizes = DEFAULT_PAGE_SIZE_OPTIONS;
  protected readonly filters = signal<ApprovalFilters>({
    searchBy: 'application',
    searchText: '',
    status: 'all',
    fromDate: '',
    toDate: '',
  });

  protected readonly contractMap = computed(() => new Map(this.contracts().map((contract) => [String(contract.id), contract])));

  protected readonly punchedApplications = computed(() =>
    this.applications().filter((row) => (row.status || '').trim().toLowerCase() === 'punched'),
  );
  protected readonly filteredApplications = computed(() => {
    const { searchBy, searchText, status, fromDate, toDate } = this.filters();
    const searchTerm = searchText.trim().toLowerCase();
    return this.punchedApplications().filter((row) => {
      const applicationDate = this.normalizeDate(row.application_date);
      const approvalState = this.approvalState(row);
      if (status !== 'all' && approvalState !== status) return false;
      if (fromDate && applicationDate && applicationDate < fromDate) return false;
      if (toDate && applicationDate && applicationDate > toDate) return false;
      if (!searchTerm) return true;
      if (searchBy === 'contract') {
        return this.contractSearchValue(row).includes(searchTerm);
      }
      return (row.application_number || '').toLowerCase().includes(searchTerm);
    });
  });
  protected readonly totalPages = computed(() => Math.max(1, Math.ceil(this.filteredApplications().length / this.pageSize())));
  protected readonly pageStart = computed(() => (this.filteredApplications().length ? ((this.currentPage() - 1) * this.pageSize()) + 1 : 0));
  protected readonly pageEnd = computed(() => Math.min(this.currentPage() * this.pageSize(), this.filteredApplications().length));
  protected readonly paginatedApplications = computed(() => {
    const start = (this.currentPage() - 1) * this.pageSize();
    return this.filteredApplications().slice(start, start + this.pageSize());
  });
  protected readonly selectedApprovalDetails = computed(() => {
    const selectedId = this.selectedApprovalDetailId();
    if (!selectedId) return null;
    return this.applications().find((item) => item.id === selectedId) || null;
  });

  ngOnInit(): void {
    this.loadApplications();
  }

  protected updateFilter<K extends keyof ApprovalFilters>(field: K, value: ApprovalFilters[K]): void {
    this.filters.update((current) => ({ ...current, [field]: value }));
    this.currentPage.set(1);
    this.loadApprovalFiles();
  }

  protected clearFilters(): void {
    this.filters.set({ searchBy: 'application', searchText: '', status: 'all', fromDate: '', toDate: '' });
    this.currentPage.set(1);
    this.loadApprovalFiles();
  }

  protected openFilePicker(input: HTMLInputElement): void {
    input.click();
  }

  protected onImportFile(application: ApplicationApi, event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    if (!files.length) return;
    if (files.length > 1) {
      this.toast.show('Only one approval file can be uploaded per application', '', 'danger');
      input.value = '';
      return;
    }

    const file = files[0];
    const name = file.name.toLowerCase();
    if (!name.endsWith('.pdf') && !name.endsWith('.txt')) {
      this.toast.show('Only PDF or TXT files are allowed', '', 'danger');
      input.value = '';
      return;
    }

    this.uploadingByApp.set({ ...this.uploadingByApp(), [application.id]: true });
    const formData = new FormData();
    formData.append('application_number', application.application_number);
    formData.append('file', file);

    this.api.post<ApprovalFile>('/api/application-approval/upload', formData).subscribe({
      next: () => {
        this.toast.show('Approval file imported successfully', '', 'success');
        this.uploadingByApp.set({ ...this.uploadingByApp(), [application.id]: false });
        input.value = '';
        this.loadApplications();
      },
      error: () => {
        this.toast.show('Failed to import approval file', '', 'danger');
        this.uploadingByApp.set({ ...this.uploadingByApp(), [application.id]: false });
        input.value = '';
      },
    });
  }

  protected approvalFilesFor(applicationNumber: string): ApprovalFile[] {
    return this.approvalFilesByApp()[applicationNumber] || [];
  }

  protected isUploading(applicationId: string): boolean {
    return !!this.uploadingByApp()[applicationId];
  }

  protected fileStatus(applicationNumber: string): '' | 'File Not Found' {
    const files = this.approvalFilesFor(applicationNumber);
    if (!files.length) return 'File Not Found';
    return '';
  }

  protected primaryApprovalFile(applicationNumber: string): ApprovalFile | null {
    return this.approvalFilesFor(applicationNumber)[0] || null;
  }

  protected approvalState(application: ApplicationApi): DerivedApprovalState {
    const hasFile = this.fileStatus(application.application_number) !== 'File Not Found';
    const approved = this.approvedQuantum(application);
    const requested = this.requestedQuantum(application);
    const approvalStatus = (application.approval_status || '').trim().toLowerCase();
    const approvalAvailable = !!application.approval_available || approvalStatus.includes('approved') || approvalStatus.includes('available');

    if (!hasFile || !approvalAvailable || approved === null || approved <= 0) {
      return 'pending';
    }

    if (requested !== null && approved >= requested) {
      return 'approved';
    }

    return 'partial-approved';
  }

  protected approvalStatusLabel(application: ApplicationApi): string {
    const state = this.approvalState(application);
    if (state === 'approved') return 'Approved';
    if (state === 'partial-approved') return 'Partial Approved';
    return 'Pending';
  }

  protected goToPage(page: number): void {
    const nextPage = Math.min(Math.max(page, 1), this.totalPages());
    if (nextPage === this.currentPage()) return;
    this.currentPage.set(nextPage);
    this.loadApprovalFiles();
  }

  protected goFirst(): void {
    this.goToPage(1);
  }

  protected goLast(): void {
    this.goToPage(this.totalPages());
  }

  protected onPageSizeChange(value: string): void {
    const nextSize = Number(value);
    if (!Number.isFinite(nextSize) || nextSize <= 0) return;
    this.pageSize.set(nextSize);
    this.currentPage.set(1);
    this.loadApprovalFiles();
  }

  protected contractDisplay(application: ApplicationApi): string {
    return this.contractMap().get(String(application.contract_id))?.contract_number || String(application.contract_id || '-');
  }

  protected applicationTypeLabel(value: string | null | undefined): string {
    return displayApplicationType(value);
  }

  protected requestedQuantum(application: ApplicationApi): number | null {
    return this.scheduleBasedQuantumMwh(application, this.asNumber(application.total_quantum));
  }

  protected approvedQuantum(application: ApplicationApi): number | null {
    return this.scheduleBasedQuantumMwh(application, this.asNumber(application.approved_quantum));
  }

  protected deviationQuantum(application: ApplicationApi): number | null {
    const requested = this.requestedQuantum(application);
    const approved = this.approvedQuantum(application);
    if (requested === null || approved === null) return null;
    return requested - approved;
  }

  protected deviationTone(application: ApplicationApi): 'positive' | 'negative' | 'neutral' {
    const deviation = this.deviationQuantum(application);
    if (deviation === null || deviation === 0) return 'neutral';
    return deviation > 0 ? 'negative' : 'positive';
  }

  protected deviationWidth(application: ApplicationApi): number {
    const requested = this.requestedQuantum(application);
    const deviation = this.deviationQuantum(application);
    if (requested === null || deviation === null || requested === 0) return 0;
    return Math.min(Math.abs((deviation / requested) * 100), 100);
  }

  protected formatQuantum(value: number | null): string {
    if (value === null) return '-';
    return Number.isInteger(value) ? value.toString() : value.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
  }

  protected formatQuantumMwh(value: number | null): string {
    const formatted = this.formatQuantum(value);
    return formatted === '-' ? '-' : `${formatted} MWh`;
  }

  protected openApprovalDetails(application: ApplicationApi): void {
    this.selectedApprovalDetailId.set(application.id);
  }

  protected closeApprovalDetails(): void {
    this.selectedApprovalDetailId.set(null);
  }

  protected approvalChargeRows(application: ApplicationApi | null): ApplicationChargeApi[] {
    return application?.approval_charges || [];
  }

  protected approvalChargeType(item: ApplicationChargeApi, index: number): string {
    const raw = `${item.sub_group_name || ''} ${item.group_name || ''} ${item.section_heading || ''}`.trim();
    return raw || `Charge ${index + 1}`;
  }

  protected approvalChargeAmount(item: ApplicationChargeApi): number {
    return Number(item.amount) || (Number(item.rate_mwh) || 0) * (Number(item.quantum_mwh) || 0);
  }

  protected downloadApprovalFile(file: ApprovalFile): void {
    window.open(this.api.resolve(`/api/application-approval/files/${file.id}/download`), '_blank');
  }

  protected markAsUnread(file: ApprovalFile): void {
    if (!file.is_read) return;
    this.api.patch<ApprovalFile>(`/api/application-approval/files/${file.id}/read-status`, { is_read: false }).subscribe({
      next: () => {
        this.toast.show('Read status updated to unread', '', 'success');
        this.loadApprovalFiles();
      },
      error: () => this.toast.show('Failed to update read status', '', 'danger'),
    });
  }

  private loadApplications(): void {
    this.loading.set(true);
    forkJoin({
      applications: this.api.get<ApplicationApi[]>('/api/applications'),
      contracts: this.api.get<Contract[]>('/api/contracts'),
    }).subscribe({
      next: ({ applications, contracts }) => {
        this.applications.set(applications || []);
        this.contracts.set(contracts || []);
        this.currentPage.set(1);
        this.loading.set(false);
        this.loadApprovalFiles();
      },
      error: () => {
        this.applications.set([]);
        this.contracts.set([]);
        this.approvalFilesByApp.set({});
        this.loading.set(false);
      },
    });
  }

  private loadApprovalFiles(): void {
    const visibleApplications = this.paginatedApplications();
    if (!visibleApplications.length) {
      this.approvalFilesByApp.set({});
      return;
    }

    forkJoin(
      visibleApplications.map((application) =>
        this.api.get<ApprovalFileListResponse>(
          `/api/application-approval/files?application_number=${encodeURIComponent(application.application_number)}&page=1&page_size=1`,
        ),
      ),
    ).subscribe({
      next: (responses) => {
        const mapped: Record<string, ApprovalFile[]> = {};
        responses.forEach((response) => {
          response.items.forEach((row) => {
            mapped[row.application_number] = [...(mapped[row.application_number] || []), row];
          });
        });
        this.approvalFilesByApp.set(mapped);
      },
      error: () => this.approvalFilesByApp.set({}),
    });
  }

  private contractSearchValue(application: ApplicationApi): string {
    return this.contractDisplay(application).toLowerCase();
  }

  private normalizeDate(value: string | null | undefined): string {
    return (value || '').trim().slice(0, 10);
  }

  protected formatDisplayDate(value: string | null | undefined): string {
    const normalized = this.normalizeDate(value);
    if (!normalized) return '-';
    const [year, month, day] = normalized.split('-');
    if (!year || !month || !day) return normalized || '-';
    return `${day}-${month}-${year}`;
  }

  private asNumber(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private parseDateOnly(value: string | null | undefined): Date | null {
    if (!value) return null;
    const [year, month, day] = value.trim().split('-').map((part) => Number(part));
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  }

  private inclusiveDayCount(start: Date, end: Date): number {
    const diffMs = end.getTime() - start.getTime();
    if (diffMs < 0) return 0;
    return Math.floor(diffMs / (24 * 60 * 60 * 1000)) + 1;
  }

  private timeDifferenceHours(fromTime: string | null | undefined, toTime: string | null | undefined): number {
    const fromMinutes = this.parseTimeToMinutes(fromTime);
    const toMinutes = this.parseTimeToMinutes(toTime);
    if (fromMinutes === null || toMinutes === null || toMinutes <= fromMinutes) return 0;
    return (toMinutes - fromMinutes) / 60;
  }

  private parseTimeToMinutes(value: string | null | undefined): number | null {
    if (!value) return null;
    const match = /^(\d{2}):(\d{2})$/.exec(value.trim());
    if (!match) return null;
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (!Number.isInteger(hours) || !Number.isInteger(minutes) || minutes < 0 || minutes >= 60) return null;
    if (hours === 24 && minutes === 0) return 24 * 60;
    if (hours < 0 || hours > 23) return null;
    return hours * 60 + minutes;
  }

  private scheduleBasedQuantumMwh(application: ApplicationApi, quantumMw: number | null): number | null {
    if (quantumMw === null || quantumMw <= 0) return quantumMw;

    const schedules = application.schedules || [];
    const totalMwh = schedules.reduce((total, schedule) => {
      const scheduleStart = this.parseDateOnly(schedule.from_date);
      const scheduleEnd = this.parseDateOnly(schedule.to_date);
      if (!scheduleStart || !scheduleEnd) return total;

      const dayDifference = this.inclusiveDayCount(scheduleStart, scheduleEnd);
      if (!dayDifference) return total;

      const timeDifference = this.timeDifferenceHours(schedule.from_time, schedule.to_time);
      if (!timeDifference) return total;

      return total + dayDifference * timeDifference * quantumMw * 250;
    }, 0);

    return totalMwh > 0 ? totalMwh : quantumMw;
  }
}
